import { useEffect, useMemo, useRef, useState } from 'react';
import { errorMessage, toastError, toastSuccess } from '../../../lib/toast';
import { fetchCompanySetting, saveCompanySetting } from '../../orgChart/orgChartApi';
import '../../orgChart/orgChart.css';

const VALID = /^\s*\d+\s*(,\s*\d+\s*)*,?\s*$/;

/**
 * Which Odoo companies a surface displays. Everything is synced from Odoo (a
 * filtered read would orphan cross-company reporting lines), so this only
 * controls what is shown. Lives in the Sync tab because that is where Odoo
 * scoping already lives.
 *
 * Parameterised rather than copied because the employee directory needs the
 * same editor against its OWN setting key: the directory is visible to every
 * employee while the org chart is gated to admins and department managers, so
 * the two scopes must be movable independently.
 */
export default function OrgChartCompaniesPanel({
  inputId = 'oc-companies-input',
  label = 'Org chart companies',
  help = 'Which Odoo companies appear on the org chart. Everything is synced from Odoo; this controls what is shown.',
  fetcher = fetchCompanySetting,
  saver = saveCompanySetting,
  savedMessage = 'Org chart companies updated',
  saveErrorMessage = 'Could not save the org chart companies.',
}) {
  const [value, setValue] = useState('');
  const [available, setAvailable] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [saving, setSaving] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    fetcher()
      .then((d) => {
        if (!active) return;
        setValue((d.configured_ids || []).join(','));
        setAvailable(d.available || []);
        setLoaded(true);
      })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [fetcher]);

  const parsed = useMemo(() => {
    if (!VALID.test(value)) return null;
    return value.split(',').map((s) => s.trim()).filter(Boolean).map(Number);
  }, [value]);

  const names = useMemo(() => {
    if (!parsed) return [];
    return parsed.map((id) => {
      const hit = available.find((c) => Number(c.id) === id);
      return { id, name: hit ? hit.name : null };
    });
  }, [parsed, available]);

  const unknown = names.filter((x) => !x.name).map((x) => x.id);

  const onSave = async () => {
    if (!parsed || parsed.length === 0 || parsed.some((x) => !Number.isInteger(x) || x < 1)) {
      setFieldError('Enter a comma separated list of company ids, for example 1,3.');
      return;
    }
    setFieldError('');
    setSaving(true);
    try {
      const d = await saver(parsed.join(','));
      if (!mountedRef.current) return;
      setValue((d.configured_ids || []).join(','));
      toastSuccess(savedMessage);
    } catch (err) {
      if (!mountedRef.current) return;
      toastError(errorMessage(err, saveErrorMessage, {
        invalid_company_ids: 'Enter a comma separated list of company ids, for example 1,3.',
      }));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  if (failed || !loaded) return null;

  return (
    <section className="oc-companies">
      <label htmlFor={inputId}>{label}</label>
      <p className="oc-companies-help">{help}</p>
      <div className="oc-companies-row">
        <input
          id={inputId}
          className="search-input"
          value={value}
          onChange={(e) => { setValue(e.target.value); setFieldError(''); }}
          placeholder="1,3"
        />
        <button type="button" className="btn" onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      {fieldError && <p className="oc-companies-error">{fieldError}</p>}
      {!fieldError && names.length > 0 && (
        <p className="oc-companies-names">
          {names.map((x) => `${x.id} ${x.name || '(unknown)'}`).join(', ')}
        </p>
      )}
      {unknown.length > 0 && (
        <p className="oc-companies-warn">
          {unknown.map((id) => `id ${id} is not in the synced data`).join(', ')}
        </p>
      )}
    </section>
  );
}
